import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { TeammateAccountGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";
import { renderGradingValue } from "./gradingHelpers.js";
import { GradingsTableContactCell } from "./gradingsTableContactCell.js";

export interface CriterionGradingsTableProps {
  criterion: PlaybookCriterionNode | undefined;
  gradings: readonly TeammateAccountGrading[];
  unfilteredEntityCount?: number;
  isLoading?: boolean;
  isError?: boolean;
  onRecordingAction: (recording: TeammateAccountGrading) => void;
  onLoadMore?: () => void;
}

export const CriterionGradingsTable: FC<CriterionGradingsTableProps> = (
  props,
) => {
  const { criterion, gradings } = props;

  const columns = useMemo(
    (): EntityTableColumnDefinition<TeammateAccountGrading>[] => [
      {
        id: "customer",
        className: "w-[34%]",
        header: "Customer",
        isRowHeader: true,
        renderCell: (grading) => <GradingsTableContactCell grading={grading} />,
      },
      {
        id: "criterionScore",
        className: "w-0",
        header: "Score",
        renderCell: (grading) => {
          if (!criterion || !grading.assessment) {
            return null;
          }

          return renderGradingValue(
            criterion.grading_value_type,
            grading.assessment.criteria_grading[criterion.id]?.value,
          );
        },
      },
      {
        id: "criterionCommentary",
        className: "w-[50%]",
        header: "Commentary",
        renderCell: (grading) => {
          if (!criterion || !grading.assessment) {
            return null;
          }

          return (
            <div>
              {grading.assessment.criteria_grading[criterion.id]?.commentary}
            </div>
          );
        },
      },
    ],
    [criterion],
  );

  return (
    <EntityTable
      aria-label="Recordings"
      columns={columns}
      entities={gradings}
      unfilteredEntityCount={props.unfilteredEntityCount}
      isLoading={props.isLoading}
      isError={props.isError}
      onLoadMore={props.onLoadMore}
      emptyMessage="No matching recordings."
      onEntityAction={props.onRecordingAction}
      size={Size.SMALL}
    />
  );
};
